import React, { useState, useEffect } from 'react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import ReactCompareImage from 'react-compare-image';
import axios from 'axios';
import { Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GalleryPage = () => {
  const [galleryItems, setGalleryItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = ['all', 'Paint Correction', 'Interior', 'Headlights', 'Ceramic Coating'];

  useEffect(() => {
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    try {
      const response = await axios.get(`${API}/gallery`);
      // Use placeholder if API returns empty array or fails
      if (response.data && response.data.length > 0) {
        setGalleryItems(response.data);
      } else {
        setGalleryItems(getPlaceholderGallery());
      }
    } catch (error) {
      console.error('Error fetching gallery:', error);
      // Use placeholder images if gallery API fails
      setGalleryItems(getPlaceholderGallery());
    } finally {
      setIsLoading(false);
    }
  };

  const getPlaceholderGallery = () => {
    return [
      {
        id: '1',
        title: 'Paint Correction & Polish',
        category: 'Paint Correction',
        description: 'Professional multi-step paint correction removing swirl marks and scratches',
        before_image: 'https://images.unsplash.com/photo-1708805282706-f44730b7e527?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwzfHxwYWludCUyMGNvcnJlY3Rpb258ZW58MHx8fHwxNzY0NDQ2Njk5fDA&ixlib=rb-4.1.0&q=85',
        after_image: 'https://images.unsplash.com/photo-1708805282695-ef186db20192?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxwYWludCUyMGNvcnJlY3Rpb258ZW58MHx8fHwxNzY0NDQ2Njk5fDA&ixlib=rb-4.1.0&q=85',
      },
      {
        id: '2',
        title: 'Premium Wheel Detailing',
        category: 'Interior',
        description: 'Deep cleaning and restoration of wheels, tires, and brake components',
        before_image: 'https://images.unsplash.com/photo-1708805282683-50a060eba80f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwzfHxhdXRvJTIwZGV0YWlsaW5nfGVufDB8fHx8MTc2NDQ0NjcxMnww&ixlib=rb-4.1.0&q=85',
        after_image: 'https://images.unsplash.com/photo-1708805283017-c662be2c7a44?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwyfHxhdXRvJTIwZGV0YWlsaW5nfGVufDB8fHx8MTc2NDQ0NjcxMnww&ixlib=rb-4.1.0&q=85',
      },
      {
        id: '3',
        title: 'Professional Paint Enhancement',
        category: 'Paint Correction',
        description: 'Machine polishing and paint enhancement for showroom finish',
        before_image: 'https://images.pexels.com/photos/6872150/pexels-photo-6872150.jpeg',
        after_image: 'https://images.pexels.com/photos/11139239/pexels-photo-11139239.jpeg',
      },
      {
        id: '4',
        title: 'Ceramic Coating Application',
        category: 'Ceramic Coating',
        description: 'Premium ceramic coating for long-lasting protection and enhanced gloss',
        before_image: 'https://images.pexels.com/photos/5233261/pexels-photo-5233261.jpeg',
        after_image: 'https://images.pexels.com/photos/6873118/pexels-photo-6873118.jpeg',
      },
    ];
  };

  const filteredItems = selectedCategory === 'all' 
    ? galleryItems 
    : galleryItems.filter(item => item.category === selectedCategory);

  return (
    <div data-testid="gallery-page" className="min-h-screen bg-black">
      <Navigation />

      {/* Hero Section */}
      <section data-testid="gallery-hero" className="relative pt-32 pb-12 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute bottom-20 left-20 w-96 h-96 bg-red-600 rounded-full filter blur-[150px]"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto text-center">
          <h1 
            data-testid="gallery-hero-title"
            className="text-5xl lg:text-6xl font-bold mb-6"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            Before & After <span className="text-red-500">Gallery</span>
          </h1>
          <p data-testid="gallery-hero-subtitle" className="text-lg text-gray-300 max-w-3xl mx-auto">
            Real transformations from our professional detailing services
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section data-testid="gallery-filters" className="py-8 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap justify-center gap-4">
            {categories.map((category) => (
              <button
                key={category}
                data-testid={`category-filter-${category}`}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-2 rounded-full transition-all ${
                  selectedCategory === category
                    ? 'bg-gradient-to-r from-red-600 to-red-800 text-white'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section data-testid="gallery-grid-section" className="py-12 px-6">
        <div className="max-w-7xl mx-auto">
          {isLoading ? (
            <div data-testid="gallery-loading" className="flex justify-center items-center py-20">
              <Loader2 className="w-12 h-12 animate-spin text-red-500" />
            </div>
          ) : filteredItems.length === 0 ? (
            <div data-testid="gallery-empty" className="text-center py-20">
              <p className="text-gray-400 text-lg">No gallery items found in this category.</p>
            </div>
          ) : (
            <div data-testid="gallery-grid" className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {filteredItems.map((item) => (
                <div key={item.id} data-testid={`gallery-item-${item.id}`} className="glass-card p-6 hover:scale-[1.02] transition-transform">
                  <div className="mb-4">
                    <ReactCompareImage
                      leftImage={item.before_image}
                      rightImage={item.after_image}
                      sliderLineColor="#E52424"
                      sliderLineWidth={4}
                    />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                  <p className="text-sm text-gray-400 mb-2">{item.description}</p>
                  <span className="inline-block px-3 py-1 bg-red-900/30 text-red-500 text-xs rounded-full">
                    {item.category}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default GalleryPage;
